import { Card, CardContent, Typography, Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { each, groupBy } from 'lodash'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PromoAndDiscountEntryDialog from '../Dialogs/Promos/PromoAndDiscountEntryDialog'
import DeletePromoConfirmation from '../Dialogs/Promos/DeletePromoConfirmation'
import InvoiceTable from '../../components/Tables/InvoiceTable'
import { filterAddOnsByTimeBlock, MEMBERSHIP_TYPES_ENUM, VIP_MEMBERSHIP_TYPES, isSignatureStayPackageAddon } from '../../utils'
import { matchesFacility } from '../../utils/facilityMatch'
import useSelectedHousehold from '../../hooks/useSelectedHousehold'
import { deleteLineItem, updateVisitLineItem } from '../../actions/lineItems/lineItems.actions'
import { createItem } from '../../actions/misc/misc.actions'

const useStyles = makeStyles((theme) => ({
	totalWrapper: {
		display: 'flex',
		justifyContent: 'flex-end'
	},
	promoWrapper: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center'
	},
	titleSection: {
		width: '150px',
		'& h6': {
			fontSize: '16px',
			lineHeight: '22px'
		}
	},
	totalSection: {
		width: '75px',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-end'
	}
}))

const VisitInvoiceCard = (props) => {
	const { visit, setIsEditItemPopupShown, setSelectedItem, addOnBank, facility } = props
	const dispatch = useDispatch()
	const household = useSelector((state) => state.specific.households.selected)
	const { isVipOrPetCareOnDate } = useSelectedHousehold()
	const visitStartDate = visit?.startDate || visit?.visit_date || visit?.dates?.[0]
	const membershipOnVisitStartDate = visitStartDate
		? isVipOrPetCareOnDate(visitStartDate, facility?._id || visit?.facility_id)
		: { vip: false, petcare: false }
	const hasFreeEarlyDropoff = membershipOnVisitStartDate.vip || membershipOnVisitStartDate.petcare

	const getEarlyDropoffDisplayPrice = (lineItemAmount) => {
		if (hasFreeEarlyDropoff) return 0
		return lineItemAmount ?? facility?.services?.daycare?.early_dropoff_fee ?? 0
	}
	const [invoice, setInvoice] = useState([])
	const [total, setTotal] = useState(0)
	const [totalBeforeTip, setTotalBeforeTip] = useState(0)
	const [surchargeDays, setSurchargeDays] = useState([])
	const [hasHolidaySurcharge, setHasHolidaySurcharge] = useState(false)
	const [amtDue, setAmtDue] = useState(0)
	const [deletePromoShown, setDeletePromoShown] = useState(false)
	const [promoModal, setPromoModal] = useState(false)
	const [totalNumber, setTotalNumber] = useState(0)

	const isExtendedVisit = visit.extended_pickup && visit.lineItems && visit.lineItems.length
	const isEarlyDropOff =
		facility?.early_dropoff_enabled && visit.early_dropoff && visit.lineItems && visit.lineItems.length

	const classes = useStyles(isExtendedVisit)
	// console.log("vis...", visit);

	useEffect(() => {
		if (visit && facility && household) {
			// setMasterHolidays();

			createInvoice()
		}
	}, [visit, facility, household, addOnBank])

	const getCreditsNeeded = () => {
		let number = 0
		visit.pets.forEach((item, i) => {
			if (i === 0) {
				number += 1
			} else {
				number += 0.5
			}
		})
		return number
	}

	const deletePromo = () => {
		const item = visit.lineItems.find((i) => i.type === 'visit_promo')
		dispatch(deleteLineItem(item))
	}

	const getPrice = () => {
		let number = 0
		let price = 0
		let additionalPrice = 0

		if (visit.type === 'daycare') {
			let needed = getCreditsNeeded()
			let inBank = 0

			let { daycare } = household.credits
			let creds = daycare.filter(
				(d) => matchesFacility(d, facility._id) && !d.expired && !d.used
			)

			if (creds) {
				inBank = creds.reduce((acc, cur) => acc + cur.credit_remaining, 0)
			}

			if (inBank >= needed) {
				price = 0
			} else {
				price = facility.services.daycare.old_visit_price || 3100
				additionalPrice = (facility.services.daycare.old_visit_price || 3100) / 2
			}
		} else if (visit.type === 'boarding') {
			price = facility.services.boarding.price_per_night
			additionalPrice = facility.services.boarding.additional_pet_price || 3000
		} else if (visit.type === 'compatibility_session') {
			price = facility.compatibility_session_price || 1000
			const memTypes = [
				MEMBERSHIP_TYPES_ENUM.pet_care_annual,
				MEMBERSHIP_TYPES_ENUM.pet_care_monthly,
				...VIP_MEMBERSHIP_TYPES
			]
			let hasMem = household?.memberships?.find(
				(membership) =>
					matchesFacility(membership, facility?._id) &&
					!membership.deactivated &&
					!moment().isAfter(moment(membership.end_date)) &&
					memTypes.includes(membership.type)
			)
			if (hasMem) price = 0

			const hadTrialMem = validateTrialPeriod(household, visit)
			if (hadTrialMem) price = 0
		} else if (visit.type === 'grooming') {
			if (visit.price >= 0) {
				price = visit.price
			} else if (visit.quoted_price_min >= 0) {
				price = visit.quoted_price_min
			} else {
				price = 5000
			}
		}

		visit.pets.forEach((item, i) => {
			if (i === 0) {
				number += price
			} else {
				number += additionalPrice
			}
		})

		return number
	}

	const validateTrialPeriod = (household, visit) => {
		const hadTrialMem = household?.memberships?.find(
			(membership) =>
				matchesFacility(membership, facility?._id || visit?.facility_id) &&
				membership.type === 'vip_trial' &&
				moment(visit.createdAt).isBetween(moment(membership.start_date), moment(membership.end_date))
		)

		return !!hadTrialMem
	}

	const getGroomingServiceType = () => {
		return visit.grooming_service_type
	}

	const getDescription = () => {
		let pets = visit.pets
		let numOfPets = pets ? pets.length : 0
		if (visit.type === 'daycare') return `Daycare Session (${numOfPets} dog${numOfPets !== 1 ? 's' : ''})`
		if (visit.type === 'boarding') return `Nights (${numOfPets} dog${numOfPets !== 1 ? 's' : ''})`
		if (visit.type === 'compatibility_session')
			return `Compatibility Session (${numOfPets} dog${numOfPets !== 1 ? 's' : ''})`
		if (visit.type === 'grooming') return `Grooming Service (${getGroomingServiceType()})`
	}

	const createInvoice = () => {
		let visitItem
		let addOnItems
		let pets = visit.pets

		let numOfPets = pets ? pets.length : 0

		let p = getPrice()

		visitItem = {
			price: getPrice(),
			quantity: visit.type !== 'boarding' ? 1 : visit.dates.length - 1,
			description: getDescription()
		}

		addOnItems = visit.add_on_services ? visit.add_on_services : []

		let bankRow = []
		if (visit?.status?.id === 5) {
			let num = 0
			visit.add_on_services.forEach((a, i) => {
				if (a.applied_bank_credit) num += a.applied_bank_credit
			})
			if (num > 0) {
				bankRow = [
					{
						price: num * -1,
						description: 'Enrichment Bank',
						quantity: 1
					}
				]
			}
		} else {
			if (addOnBank) {
				let addOnPrice = 0
				addOnItems.forEach((add, i) => {
					if (add.category !== 'meal' && add.category !== 'medication') {
						let addOn = facility.add_on_services[add.type]?.find(
							(addOnService) => addOnService._id === add.addon_id
						)

						let finalPrice = 'price' in add ? add.price : addOn ? addOn.price : 0

						// addon choice logic
						const addon_choice_id = add.addon_choice_id
						const hasAddonChoice = !!addon_choice_id && !('price' in add)
						if (hasAddonChoice && addOn?.addon_choices?.length) {
							const selectedChoice = addOn.addon_choices.find((c) => c.id === addon_choice_id)
							if (selectedChoice) {
								finalPrice += Number(selectedChoice.upcharge)
							}
						}

						addOnPrice += finalPrice
					}
				})

				if (addOnPrice) {
					let num = addOnBank
					if (addOnPrice > addOnBank) num = addOnBank
					if (addOnPrice < addOnBank) num = addOnPrice
					bankRow = [
						{
							price: num * -1,
							description: 'Enrichment Bank',
							quantity: 1,
							upcharge: 0
						}
					]
				} else {
					bankRow = []
				}
			}
		}

		let extended = []
		let early = []
		let holidayRows = []
		let days = []

		if (visit.holiday_id) {
			let h = facility.holidays.find((ho) => ho._id == visit.holiday_id)
			if (h?.surcharge_dates)
				h.surcharge_dates.forEach((d, i) => {
					// days.push(d);
					days.push({ date: d, surcharge_fee: h.surcharge_fee })
				})
		} else {
			let holiday
			visit.dates.forEach((d) => {
				let h = facility.holidays.find((ho) => !!ho.active && ho?.surcharge_dates?.includes(d))
				if (h) holiday = h
			})

			if (holiday) {
				holiday.surcharge_dates.forEach((d, i) => {
					// days.push(d);
					days.push({ date: d, surcharge_fee: holiday.surcharge_fee })
				})
			}
		}

		let ourDates = [...visit.dates]
		ourDates.pop()
		let hs = days.filter((d) => ourDates.includes(d.date))
		if (hs.length) {
			let newObj = groupBy(hs, 'surcharge_fee')

			let newArr = Object.keys(newObj)

			holidayRows = newArr.map((price) => {
				return {
					description: 'Holiday Surcharge',
					quantity: newObj[price] ? newObj[price].length : 1,
					price: Number(price)
				}
			})
		}

		if (isExtendedVisit) {
			let found = visit.lineItems.find((l) => l.type === 'extended_pickup')

			if (found) {
				extended = [
					{
						...found,
						description: `Late Pick-up`,
						subtext: found.added_by
							? `Added ${found.added_by === 'Automatically' ? '' : 'by '}${found.added_by}`
							: null,
						quantity: 1,
						price: 0
					}
				]
			}
		}

		if (isEarlyDropOff) {
			const found = visit.lineItems.find((l) => l.type === 'early_dropoff')

			if (found) {
				early = [
					{
						...found,
						description: `Early Drop Off`,
						subtext: found.added_by
							? `Added ${found.added_by === 'Automatically' ? '' : 'by '}${found.added_by}`
							: null,
						quantity: 1,
						price: getEarlyDropoffDisplayPrice(found.amount)
					}
				]
			}
		}

		let cancelFees = visit?.lineItems
			? visit.lineItems
					.filter((i) => i.type === 'petcare_cancellation')
					.map((i) => ({
						price: i.amount,
						description: 'Cancellation Fee',
						quantity: 1
					}))
			: []

		let noShowFees = visit?.lineItems
			? visit.lineItems
					.filter((i) => i.type === 'petcare_noshow')
					.map((i) => ({
						price: i.amount,
						description: 'No Show Fee',
						quantity: 1
					}))
			: []

		let visitPromos =
			visit?.lineItems
				?.filter((i) => i.type === 'visit_promo')
				.map((i) => ({
					...i,
					price: i.amount,
					description: i.description,
					quantity: 1
				})) || []

		if (noShowFees.length) {
			return setInvoice(noShowFees)
		}

		if (cancelFees.length) {
			return setInvoice(cancelFees)
		}

		if (visit.status.id === 7) {
			return setInvoice([
				{
					price: 0,
					description: 'Cancellation Fee',
					quantity: 1
				}
			])
		}

		if (visit.status.id === 8) {
			return setInvoice([
				{
					price: 0,
					description: 'No-Show Fee',
					quantity: 1
				}
			])
		}

		if (
			(facility.add_on_services && addOnItems.length > 0) ||
			(visit.boarding_options && visit.boarding_options.length)
		) {
			addOnItems = addOnItems
				.filter((addOnItem) => addOnItem.type && addOnItem.category !== 'meal')
				.filter(
					(addOnItem) =>
						visit.status?.name !== 'Completed' || addOnItem.completed || visit.type === 'grooming'
				)
				.filter((addOnItem) => filterAddOnsByTimeBlock(addOnItem, visit))
				.map((addOnItem) => {
					let addOn = facility.add_on_services[addOnItem.type]?.find(
						(addOnService) => addOnService._id === addOnItem.addon_id
					)

					const isPackageIncluded = isSignatureStayPackageAddon(addOnItem, visit, facility)
					const price = isPackageIncluded
						? 0
						: 'price' in addOnItem
							? addOnItem.price
							: addOn
								? addOn.price
								: 0
					let upcharge = 0
					// addon choice logic
					const addon_choice_id = addOnItem.addon_choice_id
					const hasAddonChoice = !!addon_choice_id
					let selectedChoice = null
					if (hasAddonChoice && addOn?.addon_choices?.length) {
						selectedChoice = addOn.addon_choices.find((c) => c.id === addon_choice_id)
						if (selectedChoice && !('price' in addOnItem) && !isPackageIncluded) {
							upcharge += Number(selectedChoice.upcharge)
							// finalPrice += upcharge
						}
					}

					// console.info({
					// 	initialPrice: addOnItem.price || addOn.price,
					// 	addOn: addOn.name,
					// 	hasAddonChoice,
					// 	price,
					// 	upcharge
					// })

					return {
						price,
						upcharge,
						quantity: 1,
						description: !hasAddonChoice
							? addOnItem.name
							: `${addOnItem.name} - ${selectedChoice?.name}`,
						completed: addOnItem.completed,
						included_in_comfort_plus: isPackageIncluded
					}
				})

			// grouping enrichments by description which is the addon name before formatting
			addOnItems = addOnItems.length > 1 ? createGroups(addOnItems) : addOnItems

			let options = []
			let boardingOptions = Array.isArray(visit.boarding_options)
				? visit.boarding_options.filter((o) => {
						let pet_ids = visit.pets.map((p) => p._id)

						return pet_ids.includes(o.pet_id) && o.kennel_type && o.kennel_type !== 'Standard'
				  })
				: []

			options = groupBy(boardingOptions, 'kennel_type')

			options = Object.keys(options)
				.map((o) => {
					let kennelOptions = options[o]
					let option = facility.kennel_options.find((ko) => ko.type === o)

					return kennelOptions && kennelOptions.length && option
						? {
								price: visit.sharing_kennels ? option.price : option.price * kennelOptions.length,
								quantity: visit.dates.length - 1,
								description: `${option.public_name}${
									visit.sharing_kennels
										? ' - Shared'
										: ` (${kennelOptions.length} dog${kennelOptions.length !== 1 ? 's' : ''})`
								}`
						  }
						: null
				})
				.filter((o) => o)

			const allItems = [
				visitItem,
				...options,
				...addOnItems,
				...extended,
				...early,
				...holidayRows,
				...bankRow,
				...cancelFees,
				...noShowFees
			]

			if (visit.type === 'grooming') {
				const totalBeforeTip = allItems.length
					? allItems.reduce(
							(accu, curr) => {
								return {
									price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
								}
							},
							{ price: 0 }
					  )
					: 0

				let desc
				let tip_amt = 0

				if (visit.tip_percent && totalBeforeTip?.price) {
					desc = `Tip (${visit.tip_percent}%)`
					tip_amt = totalBeforeTip.price * (visit.tip_percent / 100)
					tip_amt = Number((tip_amt / 100).toFixed(2)) * 100
				} else if (
					visit.manual_tip_amount ||
					visit.manual_tip_amount === 0 ||
					visit.tip ||
					visit.tip === 0
				) {
					// console.log('this one?')
					desc = `Tip`
					tip_amt =
						visit.manual_tip_amount || visit.manual_tip_amount === 0
							? visit.manual_tip_amount
							: visit.tip
				} else {
					desc = 'Tip'
					tip_amt = 0
				}

				if (visit.status.id !== 7 && visit.status.id !== 8)
					allItems.push({
						price: tip_amt,
						quantity: 1,
						description: desc,
						tip: true
					})
			}

			// console.log({ allItems, visit, totalBeforeTip })

			if (visitPromos.length) {
				const total = allItems.length
					? allItems.reduce(
							(accu, curr) => {
								return {
									price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
								}
							},
							{ price: 0 }
					  )
					: { price: 0 }

				const ourPromo = visitPromos[0]

				let ourTotal = total.price
				let promo_amt = 0
				if (Math.abs(ourPromo.amount) > ourTotal) {
					promo_amt = ourTotal
				} else {
					promo_amt = Math.abs(ourPromo.amount)
				}

				ourPromo.price = promo_amt * -1

				allItems.push(ourPromo)
			}

			setInvoice(allItems)
		} else {
			const allItems = [
				visitItem,
				...extended,
				...early,
				...holidayRows,
				...bankRow,
				...cancelFees,
				...noShowFees
			]

			if (visit.type === 'grooming') {
				const totalBeforeTip = allItems.length
					? allItems.reduce(
							(accu, curr) => {
								return {
									price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
								}
							},
							{ price: 0 }
					  )
					: 0

				let desc
				let tip_amt = 0

				if (visit.tip_percent && totalBeforeTip?.price) {
					desc = `Tip (${visit.tip_percent}%)`
					tip_amt = totalBeforeTip.price * (visit.tip_percent / 100)
					tip_amt = Number((tip_amt / 100).toFixed(2)) * 100
				} else if (
					visit.manual_tip_amount ||
					visit.manual_tip_amount === 0 ||
					visit.tip ||
					visit.tip === 0
				) {
					// console.log('we in here?')
					desc = `Tip`
					tip_amt =
						visit.manual_tip_amount || visit.manual_tip_amount === 0
							? visit.manual_tip_amount
							: visit.tip
				} else {
					desc = 'Tip'
					tip_amt = 0
				}

				if (visit.status.id !== 7 && visit.status.id !== 8)
					allItems.push({
						price: tip_amt,
						quantity: 1,
						description: desc,
						tip: true
					})
			}

			// console.log({ allItems, visit, totalBeforeTip })

			if (visitPromos.length) {
				const total = allItems.length
					? allItems.reduce(
							(accu, curr) => {
								return {
									price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
								}
							},
							{ price: 0 }
					  )
					: { price: 0 }

				const ourPromo = visitPromos[0]

				let ourTotal = total.price
				let promo_amt = 0
				if (Math.abs(ourPromo.amount) > ourTotal) {
					promo_amt = ourTotal
				} else {
					promo_amt = Math.abs(ourPromo.amount)
				}

				ourPromo.price = promo_amt * -1

				allItems.push(ourPromo)
			}

			setInvoice(allItems)
		}
	}

	const createGroups = (addOnItems) => {
		let groupedAddOnItems = groupBy(addOnItems, 'description')

		each(groupedAddOnItems, (value, key) => {
			groupedAddOnItems[key] = groupBy(groupedAddOnItems[key], 'price')
		})

		for (const type in groupedAddOnItems) {
			if (groupedAddOnItems.hasOwnProperty(type) && typeof groupedAddOnItems[type] === 'object') {
				for (const price in groupedAddOnItems[type]) {
					if (
						groupedAddOnItems[type].hasOwnProperty(price) &&
						typeof Array.isArray(groupedAddOnItems[type][price])
					) {
						groupedAddOnItems[type][price] = groupedAddOnItems[type][price].reduce((acc, cur) => {
							return {
								price: acc.price,
								quantity: acc.quantity + cur.quantity,
								description: cur.description,
								upcharge: cur.upcharge
							}
						})
					}
				}
			}
		}

		groupedAddOnItems = Object.keys(groupedAddOnItems)
			.map((groupedAddOnItem) => {
				return Object.keys(groupedAddOnItems[groupedAddOnItem]).map(
					(addOn) => groupedAddOnItems[groupedAddOnItem][addOn]
				)
			})
			.flat(1)

		return groupedAddOnItems
	}

	useEffect(() => {
		if (invoice.length > 0) {
			if (typeof invoice[0] !== 'object' || (!invoice[0].price && invoice[0].price !== 0)) {
				invoice.shift()
			}

			let updatedInvoice = invoice

			const total = updatedInvoice.reduce(
				(accu, curr) => {
					return {
						price: accu.price + (curr.price + (curr.upcharge || 0)) * curr.quantity
					}
				},
				{ price: 0 }
			)

			setTotal((total.price / 100).toFixed(2))
			setTotalNumber(total.price)
			if (visit.deposit_paid) {
				let due = total.price - visit.deposit_amount
				setAmtDue(due)
			}
		}
	}, [invoice])

	const hasPromo = visit?.lineItems?.find((i) => i.type === 'visit_promo')

	const showPromo = () => {
		setPromoModal(true)
	}

	const handlePromoDiscountOnComplete = (data) => {
		const { redeemed_promo, manual_discount } = data
		if (redeemed_promo) {
			// promo redemption happens on the api level, so nothing to do here as
			// visit will be updated in state via actions and reducer
			setPromoModal(false)
		}
		if (manual_discount) {
			const existingPromo = visit?.lineItems?.find((i) => i.type === LINE_ITEM_TYPES.VisitPromo)
			if (existingPromo) {
				manual_discount._id = existingPromo._id
				manual_discount.subtype = 'manual_discount'
				dispatch(updateVisitLineItem(existingPromo._id, manual_discount))
			} else {
				dispatch(createItem(manual_discount))
			}
			setPromoModal(false)
		}
	}

	return (
		<Card style={{ marginBottom: '48px' }}>
			<CardContent>
				<InvoiceTable
					invoice={invoice}
					visit={visit}
					facility={facility}
					setEditPromo={setPromoModal}
					setIsEditItemPopupShown={setIsEditItemPopupShown}
					setSelectedItem={setSelectedItem}
				/>
				<div>
					<div className={classes.promoWrapper}>
						<Button
							onClick={showPromo}
							style={{
								color: '#6B4AD6',
								fontSize: 14,
								letterSpacing: 1.25,
								opacity: hasPromo ? 0.5 : 1
							}}
							disabled={hasPromo ? true : false}
							variant="text">
							Add Promo
						</Button>
						<div className={classes.totalWrapper}>
							<div className={classes.titleSection}>
								<Typography variant="h6">Total:</Typography>
							</div>
							<div
								className={classes.totalSection}
								style={{
									marginRight: isExtendedVisit || isEarlyDropOff ? 52 : 0
								}}>
								<Typography variant="body" style={{ fontSize: '14px' }}>{`$${total}`}</Typography>
							</div>
						</div>
					</div>
					{visit.deposit_paid ? (
						<div className={classes.totalWrapper}>
							<div className={classes.titleSection}>
								<Typography variant="h6" style={{ fontSize: 14, fontWeight: '600' }}>
									Deposit Paid:
								</Typography>
							</div>
							<div
								className={classes.totalSection}
								style={{
									marginRight: isExtendedVisit || isEarlyDropOff ? 52 : 0
								}}>
								<Typography variant="body" style={{ fontSize: '14px' }}>{`-$${(
									visit.deposit_amount / 100
								).toFixed(2)}`}</Typography>
							</div>
						</div>
					) : null}
					{visit.deposit_paid ? (
						<div className={classes.totalWrapper}>
							<div className={classes.titleSection}>
								<Typography variant="h6">Amount Due:</Typography>
							</div>
							<div
								className={classes.totalSection}
								style={{
									marginRight: isExtendedVisit || isEarlyDropOff ? 52 : 0
								}}>
								<Typography variant="body" style={{ fontSize: '14px' }}>{`$${(
									amtDue / 100
								).toFixed(2)}`}</Typography>
							</div>
						</div>
					) : null}
				</div>
			</CardContent>
			<PromoAndDiscountEntryDialog
				open={promoModal}
				setIsShown={setPromoModal}
				price={totalNumber}
				visit={visit}
				onComplete={handlePromoDiscountOnComplete}
				onCancel={() => setPromoModal(false)}
			/>
			<DeletePromoConfirmation
				isShown={deletePromoShown}
				setIsShown={setDeletePromoShown}
				confirm={() => {
					deletePromo()
					setDeletePromoShown(false)
					setPromoModal(false)
				}}
			/>
		</Card>
	)
}

export default VisitInvoiceCard
